import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormFieldComponent } from '../../../../../../shared/components/form-field/form-field.component';
import { AzButtonComponent } from '../../../../../../shared/components/az-button/az-button.component';
import { StatusChangeModalComponent } from '../../../../../../shared/components/status-change-modal/status-change-modal.component';
import {
  LegalEntityService,
  ContactInformationDto,
} from '../../../../services/legal-entity.service';
import { UserPermissionsService } from '../../../../../../core/auth/services/user-permissions.service';

interface ContactRow {
  id: number;
  contactName: string;
  contactEmail: string;
  phone: string;
  createdDate: number;
  lastUpdatedDate: number | null;
  isEditing: boolean;
  isSaving: boolean;
  originalContactName: string;
  originalContactEmail: string;
  originalPhone: string;
  currentContactName: string;
  currentContactEmail: string;
  currentPhone: string;
}

@Component({
  selector: 'app-contact-information',
  standalone: true,
  imports: [CommonModule, FormFieldComponent, AzButtonComponent, StatusChangeModalComponent],
  templateUrl: './contact-information.component.html',
  styleUrls: ['./contact-information.component.scss'],
})
export class ContactInformationComponent implements OnChanges {
  @Input() legalEntityId: number | null = null;
  @Input() set contacts(value: ContactInformationDto[]) {
    if (value && value.length > 0) {
      this.contactRows = value.map((contact) => ({
        id: contact.id,
        contactName: contact.contact_name || '',
        contactEmail: contact.contact_email,
        phone: contact.phone || '',
        createdDate: contact.created_date,
        lastUpdatedDate: contact.last_updated_date,
        isEditing: false,
        isSaving: false,
        originalContactName: contact.contact_name || '',
        originalContactEmail: contact.contact_email,
        originalPhone: contact.phone || '',
        currentContactName: contact.contact_name || '',
        currentContactEmail: contact.contact_email,
        currentPhone: contact.phone || '',
      }));
    } else {
      // No contacts — create one empty placeholder row so the user can add the first contact
      this.contactRows = [
        {
          id: 0,
          contactName: '',
          contactEmail: '',
          phone: '',
          createdDate: 0,
          lastUpdatedDate: null,
          isEditing: false,
          isSaving: false,
          originalContactName: '',
          originalContactEmail: '',
          originalPhone: '',
          currentContactName: '',
          currentContactEmail: '',
          currentPhone: '',
        },
      ];
    }
  }

  contactRows: ContactRow[] = [];

  showContactSuccessModal = false;
  showContactProcessingModal = false;
  showComplianceWarningModal = false;
  private pendingAction: 'addContact' | 'editContact' | null = null;

  // New contact being added
  isAddingContact = false;
  newContactName = '';
  newContactEmail = '';
  newContactPhone = '';
  isSavingNewContact = false;

  constructor(
    private legalEntityService: LegalEntityService,
    public userPermissions: UserPermissionsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Reset edit/add mode when legalEntityId changes (user selected a different entity)
    if (changes['legalEntityId'] && !changes['legalEntityId'].firstChange) {
      this.isAddingContact = false;
      this.newContactName = '';
      this.newContactEmail = '';
      this.newContactPhone = '';
      this.isSavingNewContact = false;
      // Reset all contacts to non-editing state
      this.contactRows.forEach((contact) => {
        contact.isEditing = false;
        contact.isSaving = false;
      });
    }
  }

  get canEdit(): boolean {
    return this.userPermissions.hasEditContactInformationPermission();
  }

  hasChanges(contact: ContactRow): boolean {
    return (
      contact.currentContactName !== contact.originalContactName ||
      contact.currentContactEmail !== contact.originalContactEmail ||
      contact.currentPhone !== contact.originalPhone
    );
  }

  private isValidEmail(email: string): boolean {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return emailPattern.test(email.trim());
  }

  hasInvalidEmail(contact: ContactRow): boolean {
    const email = contact.isEditing ? contact.currentContactEmail : contact.contactEmail;
    return email.trim() !== '' && !this.isValidEmail(email);
  }

  get hasInvalidNewEmail(): boolean {
    return this.newContactEmail.trim() !== '' && !this.isValidEmail(this.newContactEmail);
  }

  get canSaveNewContact(): boolean {
    // Only email is required, name and phone are optional
    return this.isValidEmail(this.newContactEmail);
  }

  get isAnyContactEditing(): boolean {
    return this.contactRows.some((contact) => contact.isEditing);
  }

  toggleEdit(contact: ContactRow): void {
    if (!contact.isEditing) {
      // Entering edit mode - store original values
      contact.originalContactName = contact.contactName;
      contact.originalContactEmail = contact.contactEmail;
      contact.originalPhone = contact.phone;
      contact.currentContactName = contact.contactName;
      contact.currentContactEmail = contact.contactEmail;
      contact.currentPhone = contact.phone;
    }
    contact.isEditing = !contact.isEditing;
  }

  toggleEditAll(): void {
    // Enable edit mode for all contacts (including empty row when no contacts)
    this.contactRows.forEach((contact) => {
      contact.isEditing = true;
      // Store original values when entering edit mode
      if (!contact.originalContactName) {
        contact.originalContactName = contact.contactName;
        contact.originalContactEmail = contact.contactEmail;
        contact.originalPhone = contact.phone;
        contact.currentContactName = contact.contactName;
        contact.currentContactEmail = contact.contactEmail;
        contact.currentPhone = contact.phone;
      }
    });
  }

  discard(contact: ContactRow): void {
    // Revert changes to original values
    contact.currentContactName = contact.originalContactName;
    contact.currentContactEmail = contact.originalContactEmail;
    contact.currentPhone = contact.originalPhone;
    contact.isEditing = false;
  }

  saveChanges(contact: ContactRow): void {
    if (!contact.id || !this.legalEntityId || contact.isSaving) {
      return;
    }

    contact.isSaving = true;

    const payload = {
      id: contact.id,
      contact_name: contact.currentContactName,
      contact_email: contact.currentContactEmail,
      phone: contact.currentPhone || null,
      legal_entity_id: this.legalEntityId,
      created_date: contact.createdDate,
      last_updated_date: contact.lastUpdatedDate,
    };

    this.legalEntityService.updateContactInformation([payload]).subscribe({
      next: (responses) => {
        const response = responses[0];
        // Update the displayed values with the response
        contact.contactName = response.contact_name || '';
        contact.contactEmail = response.contact_email;
        contact.phone = response.phone || '';
        contact.lastUpdatedDate = response.last_updated_date;

        // Update original values to reflect saved state
        contact.originalContactName = response.contact_name || '';
        contact.originalContactEmail = response.contact_email;
        contact.originalPhone = response.phone || '';

        // Exit edit mode
        contact.isEditing = false;
        contact.isSaving = false;
      },
      error: () => {
        // TODO: Show error notification to user
        contact.isSaving = false;
      },
    });
  }

  saveAllChanges(): void {
    const editingContacts = this.contactRows.filter((contact) => contact.isEditing);

    if (editingContacts.length === 0 || !this.legalEntityId) {
      return;
    }

    // Mark all editing contacts as saving
    editingContacts.forEach((contact) => {
      contact.isSaving = true;
    });

    // Separate new contacts (id: 0) from existing contacts
    const newContacts = editingContacts.filter((contact) => contact.id === 0);
    const existingContacts = editingContacts.filter((contact) => contact.id > 0);

    // Handle new contacts with POST
    if (newContacts.length > 0) {
      newContacts.forEach((contact) => {
        const payload = {
          id: 0,
          contact_name: contact.currentContactName,
          contact_email: contact.currentContactEmail,
          phone: contact.currentPhone || null,
          legal_entity_id: this.legalEntityId!,
          created_date: 0,
          last_updated_date: 0,
        };

        this.legalEntityService.createContactInformation(payload).subscribe({
          next: () => {
            // Refresh the contact list after creating
            this.refreshContactList();
            contact.isSaving = false;
          },
          error: () => {
            contact.isSaving = false;
          },
        });
      });
    }

    // Handle existing contacts with PUT
    if (existingContacts.length > 0) {
      const payloads = existingContacts.map((contact) => ({
        id: contact.id,
        contact_name: contact.currentContactName,
        contact_email: contact.currentContactEmail,
        phone: contact.currentPhone || null,
        legal_entity_id: this.legalEntityId!,
        created_date: contact.createdDate,
        last_updated_date: contact.lastUpdatedDate,
      }));

      this.legalEntityService.updateContactInformation(payloads).subscribe({
        next: (responses) => {
          // Update each contact with its corresponding response
          responses.forEach((response, index) => {
            const contact = existingContacts[index];

            // Update the displayed values with the response
            contact.contactName = response.contact_name || '';
            contact.contactEmail = response.contact_email;
            contact.phone = response.phone || '';
            contact.lastUpdatedDate = response.last_updated_date;

            // Update original values to reflect saved state
            contact.originalContactName = response.contact_name || '';
            contact.originalContactEmail = response.contact_email;
            contact.originalPhone = response.phone || '';

            // Exit edit mode
            contact.isEditing = false;
            contact.isSaving = false;
          });
        },
        error: () => {
          // TODO: Show error notification to user
          existingContacts.forEach((contact) => {
            contact.isSaving = false;
          });
        },
      });
    }
  }

  onContactNameChange(contact: ContactRow, value: string): void {
    contact.currentContactName = value;
  }

  onContactEmailChange(contact: ContactRow, value: string): void {
    contact.currentContactEmail = value;
  }

  onPhoneChange(contact: ContactRow, value: string): void {
    contact.currentPhone = value;
  }

  handleDiscard(): void {
    // Discard new contact
    if (this.isAddingContact) {
      this.discardNewContact();
    }
    // Discard all editing contacts
    this.contactRows.forEach((contact) => {
      if (contact.isEditing) {
        this.discard(contact);
      }
    });
  }

  handleSave(): void {
    if (this.isAddingContact) {
      this.saveNewContact();
    } else {
      // Save all contacts being edited
      this.saveAllChanges();
    }
  }

  getDisableSaveButton(): boolean {
    if (this.isAddingContact) {
      return !this.canSaveNewContact || this.isSavingNewContact;
    } else {
      const editingContacts = this.contactRows.filter((contact) => contact.isEditing);
      if (editingContacts.length > 0) {
        const anySaving = editingContacts.some((contact) => contact.isSaving);
        // Email is required and must be valid for all contacts
        const allHaveValidEmail = editingContacts.every((contact) =>
          this.isValidEmail(contact.currentContactEmail),
        );

        // For new contacts (id: 0), check if email is filled
        // For existing contacts, check if there are changes
        const hasNewContacts = editingContacts.some((contact) => contact.id === 0);
        const hasChangesOrNewContact = hasNewContacts
          ? allHaveValidEmail
          : editingContacts.some((contact) => this.hasChanges(contact));

        return !hasChangesOrNewContact || anySaving || !allHaveValidEmail;
      }
    }
    return true;
  }

  closeComplianceWarningModal(): void {
    const action = this.pendingAction;
    this.pendingAction = null;
    this.showComplianceWarningModal = false;

    if (action === 'addContact') {
      this.isAddingContact = true;
      this.newContactName = '';
      this.newContactEmail = '';
      this.newContactPhone = '';
    } else if (action === 'editContact') {
      this.toggleEditAll();
    }
  }

  requestAddContact(): void {
    this.pendingAction = 'addContact';
    this.showComplianceWarningModal = true;
  }

  requestEditContacts(): void {
    this.pendingAction = 'editContact';
    this.showComplianceWarningModal = true;
  }

  addContact(): void {
    this.isAddingContact = true;
    this.newContactName = '';
    this.newContactEmail = '';
    this.newContactPhone = '';
  }

  discardNewContact(): void {
    this.isAddingContact = false;
    this.newContactName = '';
    this.newContactEmail = '';
    this.newContactPhone = '';
  }

  saveNewContact(): void {
    if (!this.legalEntityId || this.isSavingNewContact || !this.canSaveNewContact) {
      return;
    }

    this.isSavingNewContact = true;
    this.showContactProcessingModal = true;

    const payload = {
      id: 0,
      contact_name: this.newContactName,
      contact_email: this.newContactEmail,
      phone: this.newContactPhone || null,
      legal_entity_id: this.legalEntityId,
      created_date: 0,
      last_updated_date: 0,
    };

    this.legalEntityService.createContactInformation(payload).subscribe({
      next: () => {
        // Reset new contact form
        this.isAddingContact = false;
        this.newContactName = '';
        this.newContactEmail = '';
        this.newContactPhone = '';
        this.isSavingNewContact = false;
        this.showContactProcessingModal = false;
        // Refresh contact list from the server
        this.refreshContactList();
        // Show success modal
        this.showContactSuccessModal = true;
      },
      error: () => {
        // TODO: Show error notification to user
        this.isSavingNewContact = false;
        this.showContactProcessingModal = false;
      },
    });
  }

  private refreshContactList(): void {
    if (!this.legalEntityId) {
      return;
    }

    this.legalEntityService.getContactInformation(this.legalEntityId).subscribe({
      next: (contacts) => {
        this.contacts = contacts;
      },
      error: () => {
        // TODO: Show error notification to user
      },
    });
  }

  onNewContactNameChange(value: string): void {
    this.newContactName = value;
  }

  onNewContactEmailChange(value: string): void {
    this.newContactEmail = value;
  }

  onNewContactPhoneChange(value: string): void {
    this.newContactPhone = value;
  }

  closeContactSuccessModal(): void {
    this.showContactSuccessModal = false;
  }

  closeContactProcessingModal(): void {
    this.showContactProcessingModal = false;
  }
}
